import {
  EnvironmentInjector,
  inject,
  Injectable,
  InjectionToken,
  runInInjectionContext,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { RegistryRecords, settingsSelectors, State } from '@wam/shared';
import { IdTokenService } from '../id-token.service';
import { setRegistryDataForService } from '@app/settings/state/settings.actions';
import { ApiServiceWrapper } from './api-service.wrapper';

export const API_SERVICE_REGISTRY = new InjectionToken<Map<string, ApiServiceWrapper>>(
  'API_SERVICE_REGISTRY',
  {
    providedIn: 'root',
    factory: () => new Map<string, ApiServiceWrapper>(),
  },
);

@Injectable({
  providedIn: 'root',
})
export class ApiGatewayServiceV2 {
  private readonly injector = inject(EnvironmentInjector);
  private readonly serviceRegistry = inject(API_SERVICE_REGISTRY);
  private readonly http = inject(HttpClient);
  private readonly idTokenService = inject(IdTokenService);
  private readonly store = inject<Store<State>>(Store);

  for(service: string): ApiServiceWrapper {
    let wrapper = this.serviceRegistry.get(service);

    if (!wrapper) {
      wrapper = runInInjectionContext(this.injector, () => new ApiServiceWrapper(service));
      this.serviceRegistry.set(service, wrapper);
    }

    return wrapper;
  }

  loadRegistry(): Observable<RegistryRecords> {
    return combineLatest([
      this.idTokenService.getActiveIdToken(false),
      this.store.pipe(select(settingsSelectors.getApiSettings), take(1)),
    ]).pipe(
      switchMap(([token, apiSettings]) =>
        this.http.get<{ data: { attributes: { service: string; url: string; apiKey: string } }[] }>(
          `${apiSettings.protocol}://${apiSettings.url}/registry/v1/clients/wam/services`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ),
      ),
      map((jsonApiResult) =>
        jsonApiResult.data.reduce((acc, service) => {
          const attributes = service.attributes;
          acc[attributes.service] = { url: attributes.url, apiKey: attributes.apiKey };
          return acc;
        }, {} as RegistryRecords),
      ),
      tap((registryRecords) => this.store.dispatch(setRegistryDataForService(registryRecords))),
      take(1),
    );
  }
}
